/**
 * Limits and checks for files uploaded into templates.
 *
 * Mirrored in spt/src/helpers/file-limits.ts, fixture for fixture: the editor
 * refuses a file with the same wording before asking the server, and the
 * server refuses it again at sign and at confirm. Change both files together.
 *
 * Plan tiers follow the pricing page (templates applied per month: Lite 30,
 * Starter 100, Growth 350, Scale 700, Unlimited). Lite and Starter do not
 * support File columns. The plan itself always comes from the server
 * (GET /files/usage); this module only knows the table.
 *   filesStored    files an account may keep; 0 = no file support
 *   bytesStored    total size of those files
 *   bytesPerFile   largest single upload
 *   bytesPerApply  the sum of every file copied by one apply, counted once
 *                  per row that carries it (10 items × a 5 MB file = 50 MB)
 *   bytesPerMonth  Unlimited's fair-use cap per calendar month; null = none
 */
import { canonicalType } from '../Config';
import { templateType } from './templateLimits';

const MB = 1024 * 1024;
const GB = 1024 * MB;

const NO_FILES = Object.freeze({ filesStored: 0, bytesStored: 0, bytesPerFile: 0, bytesPerApply: 0, bytesPerMonth: null });

export const FILE_LIMITS = Object.freeze({
  lite: NO_FILES,
  starter: NO_FILES,
  growth: Object.freeze({ filesStored: 200, bytesStored: 2 * GB, bytesPerFile: 25 * MB, bytesPerApply: 100 * MB, bytesPerMonth: null }),
  scale: Object.freeze({ filesStored: 1000, bytesStored: 10 * GB, bytesPerFile: 50 * MB, bytesPerApply: 200 * MB, bytesPerMonth: null }),
  unlimited: Object.freeze({ filesStored: 2500, bytesStored: 25 * GB, bytesPerFile: 100 * MB, bytesPerApply: 200 * MB, bytesPerMonth: 500 * GB }),
});

/** Files one cell may hold. */
export const FILES_PER_CELL = 10;

/** Longest file name the server stores (template_files.name). */
export const NAME_MAX = 255;

export function supportsFiles(limits) {
  return Boolean(limits) && limits.filesStored > 0;
}

/** Extension → MIME type. The allowlist IS this map's keys. */
export const ALLOWED_TYPES = Object.freeze({
  pdf: 'application/pdf',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  txt: 'text/plain',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  csv: 'text/csv',
  ppt: 'application/vnd.ms-powerpoint',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  gif: 'image/gif',
  mp4: 'video/mp4',
});

export const ALLOWED_EXTENSIONS = Object.freeze(Object.keys(ALLOWED_TYPES));

/** For <input type="file" accept>. */
export const ACCEPT_ATTRIBUTE = ALLOWED_EXTENSIONS.map((e) => `.${e}`).join(',');

/** "Report.Final.PDF" → "pdf"; no dot, or a trailing dot → "". */
export function extensionOf(name) {
  const s = String(name == null ? '' : name).trim();
  const dot = s.lastIndexOf('.');
  if (dot <= 0 || dot === s.length - 1) return '';
  return s.slice(dot + 1).toLowerCase();
}

export function isAllowedName(name) {
  return Object.prototype.hasOwnProperty.call(ALLOWED_TYPES, extensionOf(name));
}

export function mimeFor(name) {
  return ALLOWED_TYPES[extensionOf(name)] || null;
}

const fmt = (bytes) => (bytes >= GB ? `${bytes / GB} GB` : `${Math.round(bytes / MB)} MB`);

/** "1.2 GB", "340 MB", "12 KB" for the editor. */
export function formatBytes(bytes) {
  const n = Number(bytes) || 0;
  if (n >= GB) return `${(n / GB).toFixed(n >= 10 * GB ? 0 : 1)} GB`;
  if (n >= MB) return `${(n / MB).toFixed(n >= 10 * MB ? 0 : 1)} MB`;
  return `${Math.max(1, Math.round(n / 1024))} KB`;
}

/**
 * Why `incoming` may not be added to an account at `usage`, or null when it
 * may. Same order and wording as the server.
 */
export function quotaProblem(usage, limits, incoming) {
  const name = String((incoming && incoming.name) || '').trim();
  const size = Number(incoming && incoming.size);
  if (!supportsFiles(limits)) return 'File columns are not included in your plan';
  if (!name) return 'The file needs a name';
  if (name.length > NAME_MAX) return `The file name is longer than ${NAME_MAX} characters`;
  if (!isAllowedName(name)) {
    const ext = extensionOf(name);
    return ext ? `.${ext} files are not supported` : 'Files without an extension are not supported';
  }
  if (!Number.isFinite(size) || size <= 0) return 'The file is empty';
  if (size > limits.bytesPerFile) return `A file can be at most ${fmt(limits.bytesPerFile)} on your plan`;
  if (usage.files + 1 > limits.filesStored) return `Your plan allows ${limits.filesStored} files`;
  if (usage.bytes + size > limits.bytesStored) return `Your plan allows ${fmt(limits.bytesStored)} of files`;
  return null;
}

/** Why one apply may not copy `bytes` on a plan, or null. */
export function applyBytesProblem(bytes, limits) {
  if (bytes > 0 && !supportsFiles(limits)) return 'File columns are not included in your plan';
  if (bytes > limits.bytesPerApply) return `One apply can copy at most ${fmt(limits.bytesPerApply)} of files on your plan`;
  return null;
}

const isFileCell = (cv) => cv && typeof cv === 'object' && canonicalType(String(cv.type || '')) === 'file';

function cellsOf(row, out) {
  for (const cv of Array.isArray(row && row.column_values) ? row.column_values : []) {
    if (isFileCell(cv)) out.push(Array.isArray(cv.files) ? cv.files : []);
  }
}

/** The file lists of every file cell in a save payload (subitem or group). */
export function fileCellsIn(template) {
  const out = [];
  if (templateType(template) === 'group') {
    const items = Array.isArray(template && template.content && template.content.items) ? template.content.items : [];
    for (const item of items) {
      cellsOf(item, out);
      for (const sub of Array.isArray(item && item.subitems) ? item.subitems : []) cellsOf(sub, out);
    }
  } else {
    for (const sub of Array.isArray(template && template.subitems) ? template.subitems : []) cellsOf(sub, out);
  }
  return out;
}

/** Bytes one apply of this payload copies: every file on every row, once per row. Sizes from the cells. */
export function fileBytesPerApply(template) {
  let total = 0;
  for (const files of fileCellsIn(template)) for (const f of files) total += Number(f && f.size) || 0;
  return total;
}
